import React, { useRef, useState } from 'react';
import { Download, Upload, FileText, Loader2, CheckCircle2, XCircle, Trash2, ExternalLink } from 'lucide-react';
import { storageService } from '../../agents/services/storageService';
import { processDocument } from '../services/promptGenerationService';
import { toast } from '@/hooks/use-toast';
import type { DocumentUpload } from '../types/onboarding';

const BRIEF_TEMPLATE_DOWNLOAD_URL =
  'https://docs.google.com/document/d/1evO4Xz9GW56XK_jTSi3AsAy2ZrIUfVhByIYGfIi7qFo/export?format=docx';
const BRIEF_TEMPLATE_COPY_URL =
  'https://docs.google.com/document/d/1evO4Xz9GW56XK_jTSi3AsAy2ZrIUfVhByIYGfIi7qFo/copy';

const ACCEPTED_TYPES = [
  'application/pdf',
  'text/plain',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB

function getFileType(file: File): string {
  const ext = file.name.split('.').pop()?.toLowerCase() || '';
  const map: Record<string, string> = {
    pdf: 'pdf',
    txt: 'txt',
    doc: 'docx',
    docx: 'docx',
    xls: 'xlsx',
    xlsx: 'xlsx',
  };
  return map[ext] || 'txt';
}

interface BriefDocumentUploadProps {
  documents: DocumentUpload[];
  onChange: (documents: DocumentUpload[]) => void;
}

export const BriefDocumentUpload: React.FC<BriefDocumentUploadProps> = ({ documents, onChange }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Ref to always have the latest documents for concurrent async updates
  const docsRef = useRef(documents);
  docsRef.current = documents;

  const updateDocById = (docId: string, updates: Partial<DocumentUpload>) => {
    const current = docsRef.current;
    const updated = current.map((d) =>
      d.id === docId ? { ...d, ...updates } : d,
    );
    onChange(updated);
  };

  const handleFiles = async (files: File[]) => {
    const validFiles = files.filter((file) => {
      if (!ACCEPTED_TYPES.includes(file.type)) {
        toast.error(`${file.name}: formato no soportado. Usa PDF, DOC, DOCX, XLS, XLSX o TXT.`);
        return false;
      }
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name}: excede el límite de 20MB.`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    const newDocs: DocumentUpload[] = validFiles.map((file) => ({
      id: crypto.randomUUID(),
      fileName: file.name,
      fileUrl: '',
      status: 'uploading' as const,
    }));

    onChange([...docsRef.current, ...newDocs]);

    for (let i = 0; i < validFiles.length; i++) {
      const file = validFiles[i];
      const docId = newDocs[i].id;

      try {
        const uploadResult = await storageService.uploadFile(file, 'document');
        updateDocById(docId, { fileUrl: uploadResult.url, status: 'processing' });

        const processResult = await processDocument({
          fileUrl: uploadResult.url,
          fileType: getFileType(file),
        });

        updateDocById(docId, {
          status: 'done',
          extractedText: processResult.extracted_text,
          summary: processResult.summary,
          charCount: processResult.char_count,
        });
      } catch (error: any) {
        console.error(`Error processing ${file.name}:`, error);
        updateDocById(docId, {
          status: 'error',
          error: error.message || 'Error al procesar el archivo',
        });
      }
    }
  };

  const handleRemove = (docId: string) => {
    onChange(docsRef.current.filter((d) => d.id !== docId));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    handleFiles(files);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const isProcessing = documents.some((d) => d.status === 'uploading' || d.status === 'processing');

  return (
    <div className="space-y-5">
      {/* Section 1: Download Template */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
        <div className="flex items-start gap-3">
          <Download className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-blue-900">Plantilla de Brief</p>
            <p className="text-xs text-blue-700">
              Descarga la plantilla, llénala con la información de tu negocio y luego súbela aquí.
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <a
            href={BRIEF_TEMPLATE_DOWNLOAD_URL}
            download="Plantilla_Brief.docx"
            className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Download className="w-3.5 h-3.5" />
            Descargar Plantilla (.docx)
          </a>
          <a
            href={BRIEF_TEMPLATE_COPY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-3 py-2 bg-white text-blue-700 text-xs font-medium rounded-lg border border-blue-300 hover:bg-blue-50 transition-colors"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Abrir en Google Docs
          </a>
        </div>
      </div>

      {/* Section 2: Upload Zone */}
      <div className="space-y-3">
        <p className="text-sm font-medium text-foreground">
          Sube el brief completado y documentos adicionales
        </p>
        <p className="text-xs text-muted-foreground">
          Puedes subir el brief llenado, listas de precios, catálogos, o cualquier documento relevante del negocio.
        </p>

        {/* Drop zone */}
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`
            border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
            ${isDragging
              ? 'border-primary bg-primary/5'
              : 'border-border hover:border-primary/50 hover:bg-muted/30'
            }
          `}
        >
          <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">
            Arrastra archivos aquí o <span className="text-primary font-medium">haz clic para seleccionar</span>
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            PDF, DOC, DOCX, XLS, XLSX, TXT (máx. 20MB)
          </p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.doc,.docx,.xls,.xlsx,.txt"
            onChange={handleInputChange}
            className="hidden"
          />
        </div>

        {/* File list */}
        {documents.length > 0 && (
          <div className="space-y-2">
            {documents.map((doc) => (
              <div
                key={doc.id}
                className="flex items-center gap-3 px-3 py-2.5 bg-muted/30 border border-border rounded-lg"
              >
                <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground truncate">{doc.fileName}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {doc.status === 'uploading' && (
                      <span className="flex items-center gap-1 text-xs text-blue-600">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Subiendo...
                      </span>
                    )}
                    {doc.status === 'processing' && (
                      <span className="flex items-center gap-1 text-xs text-amber-600">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Procesando...
                      </span>
                    )}
                    {doc.status === 'done' && (
                      <span className="flex items-center gap-1 text-xs text-green-600">
                        <CheckCircle2 className="w-3 h-3" />
                        Listo
                        {doc.charCount != null && (
                          <span className="text-muted-foreground ml-1">
                            ({doc.charCount.toLocaleString()} caracteres)
                          </span>
                        )}
                      </span>
                    )}
                    {doc.status === 'error' && (
                      <span className="flex items-center gap-1 text-xs text-red-600">
                        <XCircle className="w-3 h-3" />
                        {doc.error || 'Error'}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(doc.id);
                  }}
                  className="p-1 text-muted-foreground hover:text-red-500 transition-colors flex-shrink-0"
                  title="Eliminar"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {isProcessing && (
          <p className="text-xs text-muted-foreground text-center">
            Procesando documentos... Por favor espera antes de continuar.
          </p>
        )}
      </div>
    </div>
  );
};

export default BriefDocumentUpload;
